import { faFileInvoice } from '@fortawesome/pro-solid-svg-icons';
import { faCaretDown, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EditPropertyContainer } from 'components/order/overview/EditProperty/EditPropertyContainer';
import React, { useState } from 'react';
import {
  BodyText,
  BoldSmallBodyText,
  Card,
  FlexRow,
  Grid,
  Header3Text,
  Icon,
  IconSize,
  IconType,
  SmallBodyText,
  StyledTagType,
  TextTag,
  Tooltip,
} from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { ChangableStatusText } from 'components/order/overview/OrderProgress/ChangableStatusText';
import { screamingSnakeCaseToCapitalize } from 'utilities/shared/stringMethods/stringMethods';
import { copyAddressToClipboard } from '../../../utilities/order/header/copyAddressToClipboard';
import { TinyToolTipText } from './TinyToolTipText';
import { OrderNavigation } from './OrderNavigation';
import { CursorFlexRow } from './CursorFlexRow';
import { nbsp } from '../../../utilities/administration/magicStrings';
import { HeaderInformationRow } from './HeaderInformationRow';
import { OrderTagsContainer } from '../tags/OrderTagsContainer';
import { PinOrder } from 'components/shared/SearchBar/PinOrder';
import { DisplayTransactionCategory, eTransactionCategory } from '../../../ui-toolkit/enums/eTransactionType';
import { eClientIntent } from '../../../ui-toolkit/enums/eClientIntent';
import { IOrderDetailsData } from '../../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { selectFirstOrderLinkClientIntent } from '../../../ui-toolkit/orders/selectFirstOrderLinkClientIntent';
import { ZimTag } from '../overview/Summary/partiesTab/ZimTag';

export interface IOrderHeaderProps {
  order: IOrderDetailsData;
  fetchStatus: IFetchStatus;
  resetOrder: () => void;
  canCancelOrder: boolean;
}

export const OrderHeader: React.FC<IOrderHeaderProps> = ({ order, canCancelOrder, resetOrder, fetchStatus }) => {
  const [showPropertyEdit, setShowPropertyEdit] = useState<boolean>(false);

  const orderId = order.transaction.id!;

  const clientIntent = selectFirstOrderLinkClientIntent(order.orderInformation.orderLinks, order.transaction.id);
  const orderLink = order.orderInformation.orderLinks.length
    ? {
        type: order.transaction.transaction_category === eTransactionCategory.Purchase ? 'blue' : 'yellow',
        text:
          clientIntent && clientIntent !== eClientIntent.purchase
            ? clientIntent.replace(/^(.)/, (str) => str.toUpperCase())
            : undefined,
      }
    : undefined;

  return (
    <>
      {showPropertyEdit && <EditPropertyContainer onClose={() => setShowPropertyEdit(false)} />}
      <Card contentPadding="16px 16px 0 16px">
        <Grid>
          <FlexRow justifyContent={'space-between'}>
            <Grid
              columnCount={orderLink ? 6 : 5}
              columnWidth={`max-content`}
              textAlign={'center'}
              alignItems={'center'}
            >
              <SmallBodyText>
                <FontAwesomeIcon icon={faFileInvoice} />
              </SmallBodyText>
              <SmallBodyText>{order.transaction.file_number}</SmallBodyText>
              <TextTag
                text={DisplayTransactionCategory[order.transaction.transaction_category]}
                type={order.transaction.transaction_category === eTransactionCategory.Purchase ? 'blue' : 'yellow'}
              />
              <ZimTag />
              <PinOrder fileNumber={order.transaction.file_number} />
              {orderLink && (
                <TextTag
                  text={
                    <FlexRow>
                      <Icon
                        type={IconType.StackAlt}
                        color={orderLink.type === 'yellow' ? 'black' : 'white'}
                        size={IconSize.Small}
                      />
                      {orderLink.text}
                    </FlexRow>
                  }
                  type={orderLink.type as StyledTagType}
                />
              )}
            </Grid>
            <FlexRow>
              <ChangableStatusText
                canCancelOrder={canCancelOrder}
                order={order}
                resetOrder={resetOrder}
                fetchStatus={fetchStatus}
              >
                <BoldSmallBodyText>
                  {screamingSnakeCaseToCapitalize(order.orderInformation.status || '')} {nbsp}
                  <FontAwesomeIcon icon={faCaretDown} />
                </BoldSmallBodyText>
              </ChangableStatusText>
            </FlexRow>
          </FlexRow>
          <OrderTagsContainer orderId={orderId} />
          <CursorFlexRow>
            <FlexRow onClick={() => copyAddressToClipboard(order.property)}>
              <Tooltip
                tooltipContent={<TinyToolTipText message={'Click to copy full address'} />}
                placement={'top'}
                trigger={'hover'}
              >
                <Header3Text>
                  {order.property.street_address} {order.property.unit}
                </Header3Text>
              </Tooltip>
            </FlexRow>
            <FlexRow onClick={() => setShowPropertyEdit(true)}>
              <BodyText>
                <FontAwesomeIcon icon={faEdit} />
              </BodyText>
            </FlexRow>
          </CursorFlexRow>
          <HeaderInformationRow order={order} />
          <OrderNavigation orderId={orderId} />
        </Grid>
      </Card>
    </>
  );
};
